if (!customElements.get('zeya-worn-by-you')) {
  customElements.define('zeya-worn-by-you', class extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.startObserver) return;
      // Building the endless loop clones every card, and a cloned <img> gets its
      // src before its loading attribute, so the browser fetched every photo of
      // the strip (570 KB) while it was still 6,900 px below the fold. Build the
      // loop only once the strip is within 800 px of the viewport.
      if (!('IntersectionObserver' in window)) return this.start();
      this.startObserver = new IntersectionObserver((entries) => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.startObserver.disconnect();
        this.startObserver = null;
        this.start();
      }, { rootMargin: '0px 0px 800px 0px' });
      this.startObserver.observe(this);
    }
    start() {
      if (this.controller) return;
      this.gallery = this.querySelector('.zwy-gallery');
      this.cards = [...this.gallery.children].filter(card => !card.hasAttribute('data-loop-copy'));
      if (!this.cards.length) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motion.matches;
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      this.touching = false;
      this.position = 0;
      this.cycle = 0;
      this.pauseUntil = 0;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const copy = (card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('data-loop-copy', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.removeAttribute('data-shopify-editor-block');
        for (const image of clone.querySelectorAll('img')) image.alt = '';
        return clone;
      };
      this.gallery.prepend(...this.cards.map(copy));
      this.gallery.append(...this.cards.map(copy));
      this.gallery.tabIndex = 0;
      this.gallery.addEventListener('scroll', () => {
        this.normalize();
        if (!this.frame) this.position = this.gallery.scrollLeft;
      }, { ...options, passive: true });
      this.gallery.addEventListener('pointerdown', () => {
        this.touching = true;
        this.interact();
      }, options);
      const release = () => {
        if (!this.touching) return;
        this.touching = false;
        this.interact();
      };
      window.addEventListener('pointerup', release, options);
      window.addEventListener('pointercancel', release, options);
      this.gallery.addEventListener('wheel', () => this.interact(), { ...options, passive: true });
      this.gallery.addEventListener('keydown', () => this.interact(), options);
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.refresh();
      }, options);
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.refresh();
      }, options);
      this.addEventListener('focusin', () => {
        this.focused = true;
        this.refresh();
      }, options);
      this.addEventListener('focusout', () => {
        queueMicrotask(() => {
          this.focused = this.contains(document.activeElement);
          this.refresh();
        });
      }, options);
      document.addEventListener('visibilitychange', () => this.refresh(), options);
      this.motion.addEventListener('change', () => {
        this.userPaused = this.motion.matches;
        this.refresh();
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.gallery);
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.refresh();
      }, { threshold: 0.1 });
      this.visibilityObserver.observe(this);
      this.resize();
    }
    resize() {
      const progress = this.cycle ? ((this.gallery.scrollLeft % this.cycle) + this.cycle) % this.cycle / this.cycle : 0;
      this.step = this.cards[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this.gallery).gap);
      this.cycle = this.step * this.cards.length;
      this.position = this.cycle * (1 + progress);
      this.gallery.scrollLeft = this.position;
      this.refresh();
    }
    normalize() {
      if (!this.cycle) return;
      const left = this.gallery.scrollLeft;
      if (left < this.cycle - 1 || left >= this.cycle * 2) {
        const offset = ((left - this.cycle) % this.cycle + this.cycle) % this.cycle;
        this.position = this.cycle + offset;
        this.gallery.scrollLeft = this.position;
      }
    }
    interact() {
      this.pauseUntil = performance.now() + 8000;
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.refresh(), 8050);
      this.refresh();
    }
    refresh() {
      if (!this.controller) return;
      const play = this.visible && !document.hidden && !this.userPaused && !this.hovered && !this.focused && !this.touching && performance.now() >= this.pauseUntil;
      if (play && !this.frame && this.cycle) {
        this.position = this.gallery.scrollLeft;
        this.lastTime = null;
        this.frame = requestAnimationFrame(time => this.tick(time));
      } else if (!play && this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.position = this.gallery.scrollLeft;
      }
    }
    tick(time) {
      if (!this.controller) return;
      if (this.lastTime !== null) this.position += Math.min(time - this.lastTime, 50) * 0.016;
      this.lastTime = time;
      if (this.position >= this.cycle * 2) this.position -= this.cycle;
      this.gallery.scrollLeft = this.position;
      this.frame = requestAnimationFrame(next => this.tick(next));
    }
    disconnectedCallback() {
      this.startObserver?.disconnect();
      this.startObserver = null;
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      clearTimeout(this.resumeTimer);
      this.gallery?.querySelectorAll('[data-loop-copy]').forEach(copy => copy.remove());
    }
  });
}
